<script setup lang="ts">
  import { useGetAccountStates } from "@/api/account-state/account-state.ts"
  import { useGetAccount } from "@/api/account/account.ts"
  import { useGetAlertsAndBanners } from "@/api/alerts/alerts.ts"
  import { AlertSeverity, SmartPayAsYouGoState } from "@/api/model"
  import { useGetAccountId } from "@/lib/useGetAccountId.ts"
  import { cn } from "@/lib/utils"
  import { computed } from "vue"
  import AlertList from "@/components/shared/AlertList.vue"
  import Alert from "@/components/ui/alert/Alert.vue"

  import BalanceCard from "@/views/home/components/balance/BalanceCard.vue"
  import DirectDebitCard from "@/views/home/components/direct-debit/DirectDebitCard.vue"
  import MarketingBanners from "@/views/home/components/MarketingBanners.vue"
  import MeterReadingsCard from "@/views/home/components/meter-readings/MeterReadingsCard.vue"
  import { usePageLevelMaintenance } from "@/views/maintenance-banner/usePageLevelMaintenance"

  import EnergyUsageCard from "./components/energy-usage/EnergyUsageCard.vue"

  const { accountId } = useGetAccountId()

  const account = useGetAccount(accountId)
  const customerQuery = useGetAccountId()
  const accountStatesQuery = useGetAccountStates(customerQuery.accountId)
  const getAlertsAndBannersQuery = useGetAlertsAndBanners(accountId)
  const { isUnderMaintenance } = usePageLevelMaintenance()

  const isStandardAccount = computed(
    () =>
      accountStatesQuery.data.value?.smartPayAsYouGo !==
      SmartPayAsYouGoState.on,
  )

  const hasCriticalAlert = computed(() =>
    getAlertsAndBannersQuery.data.value?.alerts?.some(
      (alert) => alert.severity === AlertSeverity.critical,
    ),
  )
</script>

<template>
  <Alert
    v-if="account?.data?.value?.isClosed"
    variant="error"
    data-testid="closed-account-alert"
    class="mt-4 md:mt-0"
  >
    Your account is closed
  </Alert>
  <div v-else class="grid grid-cols-1 gap-4 sm:gap-6">
    <MarketingBanners
      :class="cn({ 'order-last': isUnderMaintenance || hasCriticalAlert })"
    />
    <AlertList :truncate-alerts="true" />
    <div
      class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6"
      data-testid="cards-container"
    >
      <template v-if="isStandardAccount">
        <BalanceCard />
        <DirectDebitCard />
      </template>
      <MeterReadingsCard />
      <EnergyUsageCard />
    </div>
  </div>
</template>
